import React, { useState } from 'react';
import { Tooltip } from '@patternfly/react-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faHistory } from '@fortawesome/pro-regular-svg-icons';
import { useNavigate } from 'react-router';
import { ZoneSetting, ZoneSettingEnum } from '../../api/zone/ZoneSetting';
import { isJsonObject } from '../../components/util/Helpers';
import { AppThemeConfig } from '../../helpers/sub-nav-contexts/sub-nav-context';
import { useMount } from 'react-use';
import { getAccessibleTextColor } from '../../utilities';
import { InovationChatRequest } from '../../api/types';
import { ChatActions } from '../../api/lambda/ChatActions';
import { useApplication } from '../../components/user/ApplicationProvider';
import { getEnvironmentCode } from '../../helpers/general-helper-functions';
import { useCommonStoreContext } from '../../components/common-store/CommonStoreProvider';
import { faSparkles } from '@fortawesome/pro-solid-svg-icons';

const FALLBACK_SUGGESTIONS = [
	'Top hospitals by revenue',
	'Trend of patient care hours',
	'Compare Q3 vs Q4',
];

const SUGGESTIONS_PROMPT =
	'Give me the 3 most common questions a user would ask about their data on this platform. ' +
	'Make sure that the length of each question does not exceed 42 text characters excluding spaces ' +
	'Reply with ONLY the 3 questions, one per line, no numbering, no extra commentary.';

function parseSuggestedQuestions(raw: string): string[] {
	if (!raw || typeof raw !== 'string') return [];

	return raw
		.split('\n')
		.map((line) =>
			line
				.trim()
				// eslint-disable-next-line no-useless-escape
				.replace(/^\d+[\.\)]\s*/, '') // strip "1. " / "1) "
				.replace(/^[-*•]\s*/, '') // strip "- " / "* " / "• "
				.trim()
		)
		.filter((line) => line.length > 0)
		.slice(0, 3);
}

/**
 * Lightens a hex color by a given percentage (0–1) by mixing it toward white.
 * e.g. lightenColor('#ef4444', 0.25) -> a lighter red/orange
 */
function lightenColor(hex: string, amount = 0.25): string {
	const normalized = hex.replace('#', '');
	const full =
		normalized.length === 3
			? normalized
					.split('')
					.map((c) => c + c)
					.join('')
			: normalized;

	const r = parseInt(full.substring(0, 2), 16);
	const g = parseInt(full.substring(2, 4), 16);
	const b = parseInt(full.substring(4, 6), 16);

	const lighten = (channel: number) => Math.round(channel + (255 - channel) * amount);

	const toHex = (channel: number) => channel.toString(16).padStart(2, '0');

	return `#${toHex(lighten(r))}${toHex(lighten(g))}${toHex(lighten(b))}`;
}

/**
 * Converts a hex color to HSL components.
 */
function hexToHsl(hex: string): { h: number; s: number; l: number } {
	const normalized = hex.replace('#', '');
	const full =
		normalized.length === 3
			? normalized
					.split('')
					.map((c) => c + c)
					.join('')
			: normalized;

	const r = parseInt(full.substring(0, 2), 16) / 255;
	const g = parseInt(full.substring(2, 4), 16) / 255;
	const b = parseInt(full.substring(4, 6), 16) / 255;

	const max = Math.max(r, g, b);
	const min = Math.min(r, g, b);
	const l = (max + min) / 2;

	let h = 0;
	let s = 0;

	if (max !== min) {
		const d = max - min;
		s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

		switch (max) {
			case r:
				h = (g - b) / d + (g < b ? 6 : 0);
				break;
			case g:
				h = (b - r) / d + 2;
				break;
			case b:
				h = (r - g) / d + 4;
				break;
		}
		h /= 6;
	}

	return { h: h * 360, s: s * 100, l: l * 100 };
}

/**
 * Converts HSL components back to a hex color string.
 */
function hslToHex(h: number, s: number, l: number): string {
	const hue = h / 360;
	const sat = s / 100;
	const light = l / 100;

	const hueToRgb = (p: number, q: number, t: number) => {
		let tt = t;
		if (tt < 0) tt += 1;
		if (tt > 1) tt -= 1;
		if (tt < 1 / 6) return p + (q - p) * 6 * tt;
		if (tt < 1 / 2) return q;
		if (tt < 2 / 3) return p + (q - p) * (2 / 3 - tt) * 6;
		return p;
	};

	let r: number;
	let g: number;
	let b: number;

	if (sat === 0) {
		r = g = b = light;
	} else {
		const q = light < 0.5 ? light * (1 + sat) : light + sat - light * sat;
		const p = 2 * light - q;
		r = hueToRgb(p, q, hue + 1 / 3);
		g = hueToRgb(p, q, hue);
		b = hueToRgb(p, q, hue - 1 / 3);
	}

	const toHex = (channel: number) =>
		Math.round(channel * 255)
			.toString(16)
			.padStart(2, '0');

	return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

/**
 * Produces a vivid, higher-contrast "bright" version of a color by boosting
 * lightness and saturation in HSL space — unlike lightenColor (which mixes
 * toward white and desaturates), this keeps the hue punchy. e.g. a dark navy
 * (#0a1f4d) becomes a vibrant bright blue rather than a muted grayish-blue.
 */
function brightenColor(hex: string, lightBoost = 15, satBoost = 20): string {
	const { h, s, l } = hexToHsl(hex);
	const newS = Math.min(100, s + satBoost);
	const newL = Math.min(85, l + lightBoost); // cap so it doesn't wash out to near-white
	return hslToHex(h, newS, newL);
}

/**
 * Builds a two-stop linear-gradient CSS string from a single base color,
 * using a vivid brightened shade (not a plain lighten) for the second stop.
 * Used specifically for the AIAnalystPanel header — other gradient usages
 * should keep using buildAccentGradient/lightenColor as-is.
 */
function buildVividAccentGradient(baseColor: string, angle = 135): string {
	const brightShade = brightenColor(baseColor);
	return `linear-gradient(${angle}deg, ${baseColor}, ${brightShade})`;
}

// Wraps the icon in a small light chip so it always renders in its native
// colors, regardless of what's behind it. This is the safe way to place the
// mark on a dark/colored background — no recoloring, no lost detail.
const AIAnalystBadge: React.FC<{
	size?: number;
	iconSize?: number;
	iconColor?: string;
	chipBackground?: string;
	style?: React.CSSProperties;
}> = ({ size = 32, iconSize, iconColor, chipBackground = '#ffffff', style }) => (
	<div
		style={{
			width: size + 10,
			height: size + 10,
			minWidth: size,
			borderRadius: '50%',
			background: chipBackground,
			display: 'flex',
			alignItems: 'center',
			justifyContent: 'center',
			boxShadow: '0 1px 2px rgba(0,0,0,0.12)',
			...style,
		}}
	>
		<FontAwesomeIcon
			fontSize={28}
			icon={faSparkles}
			color={iconColor}
		/>
	</div>
);

// "View Chat History" trigger. Navigation is passed in from the parent
// component, since hooks like useNavigate() can only be called inside a
// component (not at module scope).
const ChatHistoryButton: React.FC<{
	color?: string;
	size?: number;
	style?: React.CSSProperties;
	onClick?: () => void;
}> = ({ color = '#8a8f98', size = 14, style, onClick }) => (
	<Tooltip content="View Chat History">
		<button
			type="button"
			aria-label="View Chat History"
			style={{
				border: 'none',
				background: 'transparent',
				padding: 4,
				margin: 0,
				lineHeight: 0,
				cursor: 'pointer',
				display: 'inline-flex',
				alignItems: 'center',
				justifyContent: 'center',
				width: '20px',
				height: '20px',
				...style,
			}}
			onClick={onClick}
		>
			<FontAwesomeIcon
				icon={faHistory}
				style={{ color, fontSize: size + 15 }}
			/>
		</button>
	</Tooltip>
);

const DEFAULT_HEADER_COLOR = '#0073c3';

const AIAnalystPanel: React.FC = () => {
	const [value, setValue] = useState('');
	const [headerColor, setHeaderColor] = useState(
		() => localStorage.getItem('AppBranding_BackgroundHeaderColor') ?? DEFAULT_HEADER_COLOR
	);
	const { aiSuggestedQuestions, setAiSuggestedQuestions } = useCommonStoreContext();
	const suggestionsLoading = aiSuggestedQuestions === undefined;
	const suggestions = aiSuggestedQuestions ?? FALLBACK_SUGGESTIONS;

	const navigate = useNavigate();
	const { user } = useApplication();

	const handleAsk = (query: string = value) => {
		const trimmed = query.trim();
		if (!trimmed) return;
		navigate(`/discover/analyst?term=${trimmed}`);
	};

	useMount(() => {
		void ZoneSetting.Get(ZoneSettingEnum.applicationWindowTitle).then((response) => {
			if (response.enabled && isJsonObject(response.value)) {
				const decoded = JSON.parse(response.value) as AppThemeConfig;

				if (decoded.header_background_color) {
					localStorage.setItem(
						'AppBranding_BackgroundHeaderColor',
						decoded.header_background_color
					);
					setHeaderColor(decoded.header_background_color);
				}
			}
		});

		// Only prompt the API if the store doesn't already have an answer —
		// this is what makes it "ask once per session" instead of once per
		// mount/navigation.
		if (aiSuggestedQuestions !== undefined) return;

		void (async () => {
			try {
				const userIdentifer = user.account + getEnvironmentCode();
				const request: InovationChatRequest = {
					userId: userIdentifer,
					message: SUGGESTIONS_PROMPT,
					session_id: '',
				} as InovationChatRequest;

				const result = await ChatActions.sendInovationAIMessage(request);

				if (result.status === 'complete' && result.response) {
					// eslint-disable-next-line @typescript-eslint/no-unsafe-argument
					const parsed = parseSuggestedQuestions(result.response);
					setAiSuggestedQuestions(parsed.length === 3 ? parsed : FALLBACK_SUGGESTIONS);
				} else {
					// 'pending' or 'error': store the fallback so we don't
					// keep re-prompting every mount for a request that
					// isn't succeeding.
					setAiSuggestedQuestions(FALLBACK_SUGGESTIONS);
				}
			} catch (err) {
				console.error('Failed to fetch suggested questions:', err);
				setAiSuggestedQuestions(FALLBACK_SUGGESTIONS);
			}
		})();
	});

	return (
		<div
			style={{
				width: '100%',
				boxSizing: 'border-box',
				display: 'flex',
				borderRadius: 16,
				overflow: 'hidden',
				border: '1px solid #e4e6ea',
				boxShadow: '0 2px 6px rgba(16,24,40,0.04)',
			}}
		>
			<div
				style={{
					background: buildVividAccentGradient(headerColor),
					padding: '20px 22px',
					minWidth: 220,
					display: 'flex',
					flexDirection: 'column',
					justifyContent: 'center',
					gap: 4,
				}}
			>
				<AIAnalystBadge
					size={34}
					iconSize={20}
					style={{ marginBottom: 6 }}
					iconColor={headerColor}
				/>
				<span
					style={{
						color: getAccessibleTextColor(headerColor ?? '#002E57'),
						fontWeight: 800,
						fontSize: 17,
					}}
				>
					AI Analyst
				</span>
				<span
					style={{
						color: getAccessibleTextColor(headerColor ?? '#002E57'),
						fontSize: 12.5,
					}}
				>
					Powered by your live data
				</span>
			</div>

			<div style={{ background: '#fff', padding: 18, flex: 1 }}>
				<div
					style={{
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'space-between',
						marginBottom: 8,
					}}
				>
					<div
						style={{
							fontSize: 11,
							fontWeight: 700,
							letterSpacing: 1,
							color: '#8a8f98',
							textTransform: 'uppercase',
						}}
					>
						Try asking
					</div>
					<ChatHistoryButton
						color="#b0b3b9"
						size={14}
						onClick={() => navigate('/discover/analyst')}
					/>
				</div>
				<div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 14 }}>
					{suggestions.map((s) => (
						<button
							key={s}
							type="button"
							style={{
								border: 'none',
								background: lightenColor(headerColor, 0.85),
								color: headerColor,
								borderRadius: 999,
								padding: '6px 12px',
								fontSize: 12.5,
								cursor: 'pointer',
								fontWeight: 'bold',
							}}
							onClick={() => handleAsk(s)}
						>
							{s}
						</button>
					))}
				</div>
				<div
					style={{
						display: 'flex',
						alignItems: 'center',
						background: '#f2f3f5',
						borderRadius: 999,
						padding: '6px 6px 6px 14px',
						gap: 10,
					}}
				>
					<input
						value={value}
						onChange={(e) => setValue(e.target.value)}
						onKeyDown={(e) => e.key === 'Enter' && handleAsk()}
						placeholder="Type your question here..."
						style={{
							flex: 1,
							border: 'none',
							background: 'transparent',
							outline: 'none',
							fontSize: 14,
							color: '#1a1a1a',
							width: '100%',
						}}
					/>
					<button
						type="button"
						style={{
							border: 'none',
							background: headerColor,
							color: getAccessibleTextColor(headerColor ?? '#002E57'),
							borderRadius: 999,
							padding: '7px 16px',
							fontSize: 13,
							fontWeight: 700,
							cursor: 'pointer',
						}}
						onClick={() => handleAsk()}
					>
						Ask
					</button>
				</div>
			</div>
		</div>
	);
};

export default AIAnalystPanel;
